/**
 * Sentry Error Tracking Configuration
 * 
 * Captura e monitora erros em produção com context rico:
 * - User ID e email
 * - Tenant ID e nome
 * - Navegação e breadcrumbs
 * - Stack traces legíveis (source maps)
 * 
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from '@sentry/react';

/**
 * Verifica se o Sentry está habilitado
 */
export const isSentryEnabled = (): boolean => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return !!dsn && dsn.length > 0;
};

/**
 * Inicializa o Sentry com todas as integrações
 */
export const initSentry = (): void => {
  // Só inicializar se DSN estiver configurado
  if (!isSentryEnabled()) {
    console.log('[Sentry] Desabilitado (VITE_SENTRY_DSN não configurado)');
    return;
  }

  console.log('[Sentry] Inicializando...');

  try {
    Sentry.init({
      // DSN do projeto
      dsn: import.meta.env.VITE_SENTRY_DSN,
      
      // Environment (development, staging, production)
      environment: import.meta.env.MODE,
      
      // Release tracking (versão do app)
      release: `imobpanda@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
      
      // Integrações (configuração básica - BrowserTracing e Replay são opcionais)
      integrations: [
        // Integrações padrão do Sentry React
        // BrowserTracing e Replay serão adicionados automaticamente se disponíveis
      ],
      
      // Performance Monitoring (opcional - desabilitado por padrão)
      // Habilitar apenas se necessário para economizar quota
      tracesSampleRate: 0, // Desabilitado (pode habilitar com 0.1 em produção)
      
      // Filtro de erros antes de enviar
      beforeSend(event, hint) {
        const error = hint.originalException;
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // 1. Ignorar chunk loading errors (já tratados pelo ErrorBoundary)
        if (
          errorMessage.includes('Loading chunk') ||
          errorMessage.includes('ChunkLoadError') ||
          errorMessage.includes('Loading CSS chunk') ||
          errorMessage.includes('Loading module') ||
          errorMessage.includes('Importing a module script failed')
        ) {
          console.log('[Sentry] Ignorando chunk loading error');
          return null;
        }
        
        // 2. Ignorar erros de network (já tratados)
        if (
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network request failed')
        ) {
          console.log('[Sentry] Ignorando network error');
          return null;
        }
        
        // 3. Ignorar erros de extensões do browser
        if (
          errorMessage.includes('chrome-extension://') ||
          errorMessage.includes('moz-extension://') ||
          errorMessage.includes('safari-extension://')
        ) {
          console.log('[Sentry] Ignorando browser extension error');
          return null;
        }
        
        // 4. Remover dados sensíveis
        if (event.request) {
          // Remover cookies
          delete event.request.cookies;
          
          // Remover headers sensíveis
          if (event.request.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.Cookie;
          }
        }
        
        // 5. Adicionar tags customizadas
        event.tags = {
          ...event.tags,
          'app.version': import.meta.env.VITE_APP_VERSION || '1.0.0',
          'app.environment': import.meta.env.MODE,
        };
        
        return event;
      },
      
      // Filtro de breadcrumbs
      beforeBreadcrumb(breadcrumb, hint) {
        // Não capturar console.log em produção (muito verboso)
        if (breadcrumb.category === 'console' && import.meta.env.PROD) {
          return null;
        }
        
        // Mascarar dados sensíveis em breadcrumbs de fetch
        if (breadcrumb.category === 'fetch' && breadcrumb.data) {
          // Remover tokens de URLs
          if (breadcrumb.data.url) {
            breadcrumb.data.url = breadcrumb.data.url.replace(
              /([?&])(token|key|password|secret)=[^&]*/gi,
              '$1$2=***'
            );
          }
        }
        
        return breadcrumb;
      },
      
      // Ignorar erros específicos
      ignoreErrors: [
        // Erros de browser/extensões
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'atomicFindClose',
        
        // Erros de network que não podemos controlar
        'Non-Error promise rejection captured',
        
        // Erros de ResizeObserver (não críticos)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],
      
      // Denylist de URLs (não capturar erros desses domínios)
      denyUrls: [
        // Extensões do browser
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        
        // Scripts de terceiros
        /graph\.facebook\.com/i,
        /connect\.facebook\.net/i,
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
      ],
    });

    console.log('[Sentry] Inicializado com sucesso');
    
    // Configurar tags globais
    Sentry.setTags({
      'app.name': 'ImobPanda',
      'app.version': import.meta.env.VITE_APP_VERSION || '1.0.0',
      'app.environment': import.meta.env.MODE,
    });
    
  } catch (error) {
    console.error('[Sentry] Erro ao inicializar:', error);
  }
};

/**
 * Captura uma exceção manualmente
 */
export const captureException = (
  error: Error | unknown,
  context?: Sentry.CaptureContext
): string => {
  if (!isSentryEnabled()) {
    console.error('[Sentry] Error (not sent - disabled):', error);
    return '';
  }
  
  return Sentry.captureException(error, context);
};

/**
 * Captura uma mensagem manualmente
 */
export const captureMessage = (
  message: string,
  level?: Sentry.SeverityLevel
): string => {
  if (!isSentryEnabled()) {
    console.log(`[Sentry] Message (not sent - disabled) [${level}]:`, message);
    return '';
  }
  
  return Sentry.captureMessage(message, level);
};

/**
 * Adiciona um breadcrumb manualmente
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Define o usuário atual
 */
export const setUser = (user: Sentry.User | null): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  Sentry.setUser(user);
};

/**
 * Define um contexto customizado
 */
export const setContext = (name: string, context: Record<string, any> | null): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  Sentry.setContext(name, context);
};

/**
 * Define tags customizadas
 */
export const setTags = (tags: Record<string, string>): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  Sentry.setTags(tags);
};

/**
 * Define uma tag customizada
 */
export const setTag = (key: string, value: string): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  Sentry.setTag(key, value);
};

// Exportar Sentry para uso direto quando necessário
export { Sentry };
